import React, { useState, useEffect } from 'react';
import siteWash from './siteWash';
import AeroIcon from './AeroIcon';

/**
 * The v3 Home parts (research/v3/make, m1 and m2), as small views fed by real
 * data on Home and by Example data on /aero. Flat v3 styles in
 * aero-ut-theme.css (render inside .ut-theme):
 *
 *   AeroStatus       a status word with its dot: live, open, closed, draft, new, priv
 *   AeroSessionDots  one dot per session: done, noaudio (struck ring), processing (dashed)
 *   AeroTimeline     the "What's new" list; AeroTimelineDay heads each day
 *   AeroEventRow     one event: time, its mark on the line, subject, who, text, action
 *   AeroSiteBlock    a site as a block: preview, name, address, status
 *   AeroTestLine     a test as one status line: preview, name, sessions, dots, status
 *   AeroEmptyPanel   a first-run panel on a step wash
 *
 * Event kinds (one mark each, no icon tiles, no coloured stripes):
 *   ses (session arrived), proc (transcribing), noaud (no audio), review (summary
 *   ready), pub (published), made (test made), visits (site visits), soon (Free
 *   site offline soon). visits and soon are drawn for the catalogue only: the
 *   app has no events feed for them yet.
 */

export const EVENT_KINDS = [
  ['ses', 'Session arrived'],
  ['proc', 'Transcribing'],
  ['noaud', 'No audio'],
  ['review', 'Summary ready'],
  ['pub', 'Published'],
  ['made', 'Test made'],
  ['visits', 'Site visits'],
  ['soon', 'Offline soon (Free)'],
];

const STATUS_WORDS = { live: 'Live', open: 'Open', closed: 'Closed', draft: 'Draft', new: 'New', priv: 'Password', off: 'Not published' };

export function AeroStatus({ kind = 'live', children }) {
  return (
    <span className={`aero-st aero-st-${kind}`}>
      {kind === 'priv' && <AeroIcon name="lock" />}
      {children || STATUS_WORDS[kind]}
    </span>
  );
}

export function AeroSessionDots({ dots = [] }) {
  if (!dots.length) return null;
  return (
    <span className="aero-sdots" aria-hidden="true">
      {dots.map((d, i) => <i key={i} className={d === 'done' ? '' : `is-${d}`} />)}
    </span>
  );
}

export function AeroMark({ kind }) {
  return <span className={`aero-mk aero-mk-${kind}`} aria-hidden="true" />;
}

export function AeroTimeline({ children, className = '' }) {
  return <div className={`aero-tl ${className}`.trim()}>{children}</div>;
}

export function AeroTimelineDay({ label, date }) {
  return (
    <div className="aero-tl-day">
      {label}
      {date && <span>{date}</span>}
    </div>
  );
}

export function AeroEventRow({ time, kind = 'ses', subject, who, children, action = null, onOpen }) {
  const Body = onOpen ? 'button' : 'div';
  return (
    <div className="aero-ev">
      <span className="aero-ev-t">{time}</span>
      <AeroMark kind={kind} />
      <Body type={onOpen ? 'button' : undefined} className="aero-ev-b" onClick={onOpen}>
        <span className="aero-ev-h">
          <b>{subject}</b>
          {who && (
            <span className="aero-ev-who">
              <span className="aero-ev-av" aria-hidden="true">{who.charAt(0).toUpperCase()}</span>
              {who}
            </span>
          )}
        </span>
        <span className="aero-ev-text">{children}</span>
      </Body>
      <div className="aero-ev-a">{action}</div>
    </div>
  );
}

/**
 * The preview tile (decided 25 Oct 2026, DECISIONS .7): a stored snapshot of
 * the real page, cropped to its top, with the wash behind and around it built
 * from the snapshot's own colours (previewColors, via siteWash). Without a
 * snapshot: the favicon or the first letter on the step's wash. The image
 * loads lazily and fades in; the tile keeps its size, so nothing jumps.
 * previewUrl / previewColors are written on site and test docs by the capture
 * pipeline (a separate piece of work); this only shows them.
 */
export function AeroPreviewTile({ step = 'publish', name = '', favicon = null, previewUrl = null, previewColors = null, className = '' }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setLoaded(false); setFailed(false); }, [previewUrl]);
  const shot = previewUrl && !failed;
  const wash = shot ? siteWash(previewColors && previewColors.length ? previewColors : null, { card: true }) : null;
  return (
    <span
      className={`aero-pv aero-pv-${step}${shot ? ' aero-pv-shot' : ''} ${className}`.trim()}
      style={wash ? { background: wash } : undefined}
      aria-hidden="true"
    >
      {shot ? (
        <img
          className={`aero-pv-img${loaded ? ' is-loaded' : ''}`}
          src={previewUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      ) : favicon ? (
        <img src={favicon} alt="" />
      ) : (
        <b>{(name || '?').trim().charAt(0).toUpperCase()}</b>
      )}
    </span>
  );
}

export function AeroSiteBlock({ name, address, status = 'live', favicon = null, previewUrl = null, previewColors = null, example = false, preview = null, onOpen }) {
  const Tag = onOpen ? 'button' : 'div';
  return (
    <Tag type={onOpen ? 'button' : undefined} className="aero-sblock" onClick={onOpen}>
      {preview || <AeroPreviewTile step="publish" name={name} favicon={favicon} previewUrl={previewUrl} previewColors={previewColors} />}
      <span className="aero-sblock-meta">
        <b>
          {name}
          {example && <span className="aero-smp">Example</span>}
        </b>
        <span className="aero-sblock-addr">{address}</span>
        <AeroStatus kind={status} />
      </span>
    </Tag>
  );
}

export function AeroTestLine({ name, meta, dots = [], status = 'open', statusLabel, previewUrl = null, previewColors = null, example = false, preview = null, newCount = 0, onOpen }) {
  const Tag = onOpen ? 'button' : 'div';
  return (
    <Tag type={onOpen ? 'button' : undefined} className="aero-tline" onClick={onOpen}>
      {preview || <AeroPreviewTile step="test" name={name} previewUrl={previewUrl} previewColors={previewColors} />}
      <span className="aero-tline-n">
        <b>{name}{example && <span className="aero-smp">Example</span>}</b>
        {(meta || newCount > 0) && (
          <small>
            {newCount > 0 && <span className="aero-tline-new">{newCount} new</span>}
            {newCount > 0 && meta ? ' · ' : null}
            {meta}
          </small>
        )}
      </span>
      <AeroSessionDots dots={dots} />
      <AeroStatus kind={status}>{statusLabel}</AeroStatus>
    </Tag>
  );
}

export function AeroEmptyPanel({ wash = 'publish', title, lead, children, aside = null, className = '' }) {
  return (
    <section className={`aero-empty-panel aero-w-${wash} ${className}`.trim()}>
      <div className="aero-empty-panel-main">
        <h2 className="aero-empty-panel-title">
          {title}
          <span className="aero-dot-stop" aria-hidden="true" />
        </h2>
        {lead && <p className="aero-empty-panel-lead">{lead}</p>}
        {children}
      </div>
      {aside && <div className="aero-empty-panel-aside">{aside}</div>}
    </section>
  );
}

/** One way in (first run): an icon, a title, a line, and the whole row opens it. */
export function AeroWayRow({ icon, title, desc, onClick }) {
  return (
    <button type="button" className="aero-way" onClick={onClick}>
      <AeroIcon name={icon} />
      <span>
        <b>{title}</b>
        <small>{desc}</small>
      </span>
      <AeroIcon name="right" className="aero-way-go" />
    </button>
  );
}
